import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { FormSubscribe, Wizard, WizardStep, toast, useWizard } from "@performyard/py-components";

import { ExitWizardModal } from "../../../components/ExitWizardModal";
import { WizardStepWrapper } from "../../../components/WizardStep";
import { LaunchingLoader } from "./components/LaunchingLoader";

import { useLaunchWorkflow, useWorkflowLaunchJob, workflowsKeys } from "../../../api/hooks/defs/workflows";
import useUnsavedChanges from "../../../hooks/useUnsavedChanges";

import { getUserSelectionCount } from "../../../api/services/defs/user-selection";
import { describeLaunchWorkflowError } from "../../../api/services/defs/workflows";

import {
	type AssigneeValue,
	EMPLOYEES_STEP_ID,
	EMPLOYEES_STEP_TITLE,
	GENERAL_INFO_STEP_ID,
	GENERAL_INFO_STEP_TITLE,
	GeneralInfoSchema,
	type LaunchTodoRow,
	PREVIEW_STEP_ID,
	PREVIEW_STEP_TITLE,
	TODOS_STEP_ID,
	TODOS_STEP_TITLE,
	buildLaunchPayload,
	getGeneralInfoDefaults,
	hasManagerAssignment,
	readEmployeesStepData,
	readGeneralInfo,
	todoRowsHaveErrors,
} from "./launch-form";
import { GeneralInfoStep } from "./steps/GeneralInfoStep";
import { PreviewStep } from "./steps/PreviewStep";
import { SelectEmployeesStep } from "./steps/SelectEmployeesStep";
import { TodosStep } from "./steps/TodosStep";

const WIZARD_TITLE = "Launch Workflow";

const EXIT_MESSAGE = "Your workflow configuration will not be saved. Are you sure you want to exit?";

/**
 * The launch job creates one workflow per subject, so a large selection can outlast any
 * reasonable wait. The loader is a courtesy, not a gate: once the limit passes the user
 * moves on to the workflows list, where the workflows appear as the job creates them.
 */
const LAUNCH_JOB_WAIT_LIMIT_MS = 60_000;

/**
 * The to-do rows are seeded from the chosen template, so changing the template has to
 * invalidate everything downstream of step 1. Mounted inside step 1 (the only place the
 * template can change) and mirrors the review-cycle wizard's `GroupChangeSync`: without
 * the `resetStepsFrom`, the stepper would still let the user click straight back to an
 * already-visited Preview and launch the new template with the old template's to-dos.
 */
const TemplateChangeSync = ({
	templateId,
	onTemplateChanged,
}: {
	templateId: string | null;
	onTemplateChanged: () => void;
}) => {
	const { resetStepsFrom, steps } = useWizard();
	const prevTemplateIdRef = useRef(templateId);

	useEffect(() => {
		if (prevTemplateIdRef.current !== templateId) {
			prevTemplateIdRef.current = templateId;
			const todosIndex = steps.findIndex((step) => step.id === TODOS_STEP_ID);
			if (todosIndex !== -1) {
				resetStepsFrom(todosIndex);
			}
			onTemplateChanged();
		}
	}, [templateId, onTemplateChanged, resetStepsFrom, steps]);

	return null;
};

type LaunchWorkflowWizardProps = {
	onExit: () => void;
	/** Called once the launch job has finished (or outlasted the wait) — the page leaves the wizard. */
	onLaunched: () => void;
};

export const LaunchWorkflowWizard = ({ onExit, onLaunched }: LaunchWorkflowWizardProps) => {
	const [showExitConfirm, setShowExitConfirm] = useState(false);
	const [suppressUnsavedChanges, setSuppressUnsavedChanges] = useState(false);

	const [userSelectionId, setUserSelectionId] = useState<string | null>(null);
	const [showSelectionError, setShowSelectionError] = useState(false);

	const [rows, setRows] = useState<LaunchTodoRow[]>([]);
	const [loadedTemplateId, setLoadedTemplateId] = useState<string | null>(null);
	const [showTodoErrors, setShowTodoErrors] = useState(false);

	const [backupManager, setBackupManager] = useState<AssigneeValue | null>(null);
	const [showBackupManagerError, setShowBackupManagerError] = useState(false);
	const [launchError, setLaunchError] = useState<string | null>(null);

	const [launchJobId, setLaunchJobId] = useState<string | null>(null);
	const [hasWaitedOutJob, setHasWaitedOutJob] = useState(false);

	const queryClient = useQueryClient();
	const { mutateAsync: launchWorkflow, isPending: isLaunching, isSuccess: hasLaunched } = useLaunchWorkflow();
	const { data: launchJob, isError: launchJobFailed } = useWorkflowLaunchJob(launchJobId);
	const { setIsUnsafe, setIsSafe } = useUnsavedChanges({
		message: "You have unsaved changes. Are you sure you want to leave this page?",
		disabled: suppressUnsavedChanges || isLaunching || hasLaunched,
	});

	useEffect(() => {
		if (!launchJobId) return;
		const timer = window.setTimeout(() => setHasWaitedOutJob(true), LAUNCH_JOB_WAIT_LIMIT_MS);
		return () => window.clearTimeout(timer);
	}, [launchJobId]);

	const isJobDone = !!launchJob?.finished || launchJobFailed || hasWaitedOutJob;

	const hasLeftForJobRef = useRef(false);
	useEffect(() => {
		if (!launchJobId || !isJobDone || hasLeftForJobRef.current) return;
		hasLeftForJobRef.current = true;
		queryClient.invalidateQueries({ queryKey: workflowsKeys.all });
		onLaunched();
	}, [launchJobId, isJobDone, queryClient, onLaunched]);

	const handleExitRequest = (): void => {
		setSuppressUnsavedChanges(true);
		setShowExitConfirm(true);
	};

	const handleExitConfirm = (): void => {
		setIsSafe();
		onExit();
	};

	const handleTemplateChanged = useCallback((): void => {
		setRows([]);
		setLoadedTemplateId(null);
		setShowTodoErrors(false);
	}, []);

	const hasManagerAssigned = hasManagerAssignment(rows);

	const employeesBeforeNext = useCallback(async () => {
		if (!userSelectionId) {
			setShowSelectionError(true);
			return false;
		}
		const data = await getUserSelectionCount(userSelectionId);
		if ((data?.count ?? 0) === 0) {
			setShowSelectionError(true);
			return false;
		}
		setShowSelectionError(false);
		return true;
	}, [userSelectionId]);

	const todosBeforeNext = useCallback((): boolean => {
		if (rows.length === 0 || todoRowsHaveErrors(rows)) {
			setShowTodoErrors(true);
			return false;
		}
		setShowTodoErrors(false);
		return true;
	}, [rows]);

	const previewBeforeNext = useCallback((): boolean => {
		if (hasManagerAssigned && !backupManager) {
			setShowBackupManagerError(true);
			return false;
		}
		setShowBackupManagerError(false);
		return true;
	}, [hasManagerAssigned, backupManager]);

	const handleComplete = async (wizardData: Record<string, unknown>): Promise<void> => {
		const generalInfo = readGeneralInfo(wizardData);
		const employees = readEmployeesStepData(wizardData);
		if (!generalInfo || !employees?.userSelectionId) return;

		setLaunchError(null);
		try {
			const response = await launchWorkflow(
				buildLaunchPayload({
					generalInfo,
					userSelectionId: employees.userSelectionId,
					backupManager: hasManagerAssigned ? backupManager : null,
					rows,
				})
			);
			setIsSafe();
			setLaunchJobId(response.jobID);
		} catch (error) {
			Sentry.captureException(error);
			const message = describeLaunchWorkflowError(error);
			setLaunchError(message);
			toast.error(message);
		}
	};

	if (launchJobId) {
		return <LaunchingLoader />;
	}

	return (
		<>
			<ExitWizardModal
				isOpen={showExitConfirm}
				message={EXIT_MESSAGE}
				onClose={() => {
					setShowExitConfirm(false);
					setSuppressUnsavedChanges(false);
				}}
				onConfirm={handleExitConfirm}
			/>
			<Wizard
				id="launch-workflow-wizard"
				title="Creation Steps"
				submitLabel="Launch Workflow"
				onComplete={handleComplete}
				onExit={handleExitRequest}
				isSubmitting={isLaunching}
				onStepChange={() => {
					setIsUnsafe();
					setShowSelectionError(false);
					setLaunchError(null);
				}}
			>
				<WizardStep
					id={GENERAL_INFO_STEP_ID}
					title={GENERAL_INFO_STEP_TITLE}
					schema={GeneralInfoSchema}
					defaultValues={getGeneralInfoDefaults()}
				>
					{() => (
						<WizardStepWrapper title={WIZARD_TITLE} subtitle={GENERAL_INFO_STEP_TITLE}>
							<GeneralInfoStep onDirty={setIsUnsafe} />
							<FormSubscribe
								id="general-info-template-watcher"
								selector={(state) => (state.values?.template as { id?: string } | null)?.id ?? null}
							>
								{(templateId) => (
									<TemplateChangeSync
										templateId={templateId}
										onTemplateChanged={handleTemplateChanged}
									/>
								)}
							</FormSubscribe>
						</WizardStepWrapper>
					)}
				</WizardStep>

				<WizardStep id={EMPLOYEES_STEP_ID} title={EMPLOYEES_STEP_TITLE} beforeNext={employeesBeforeNext}>
					{() => (
						<SelectEmployeesStep
							stepTitle={WIZARD_TITLE}
							userSelectionId={userSelectionId}
							onSelectionCreated={setUserSelectionId}
							showSelectionError={showSelectionError}
						/>
					)}
				</WizardStep>

				<WizardStep id={TODOS_STEP_ID} title={TODOS_STEP_TITLE} beforeNext={todosBeforeNext}>
					{({ wizardData }) => (
						<TodosStep
							stepTitle={WIZARD_TITLE}
							templateId={readGeneralInfo(wizardData)?.template?.id ?? null}
							rows={rows}
							onRowsChange={setRows}
							loadedTemplateId={loadedTemplateId}
							onTemplateLoaded={setLoadedTemplateId}
							showErrors={showTodoErrors}
						/>
					)}
				</WizardStep>

				<WizardStep id={PREVIEW_STEP_ID} title={PREVIEW_STEP_TITLE} beforeNext={previewBeforeNext}>
					{({ wizardData }) => (
						<WizardStepWrapper title={WIZARD_TITLE} subtitle={PREVIEW_STEP_TITLE}>
							<PreviewStep
								generalInfo={readGeneralInfo(wizardData)}
								selectedCount={readEmployeesStepData(wizardData)?.selectedCount ?? 0}
								rows={rows}
								hasManagerAssigned={hasManagerAssigned}
								backupManager={backupManager}
								onBackupManagerChange={(value) => {
									setBackupManager(value);
									if (value) setShowBackupManagerError(false);
								}}
								showBackupManagerError={showBackupManagerError}
								launchError={launchError}
							/>
						</WizardStepWrapper>
					)}
				</WizardStep>
			</Wizard>
		</>
	);
};
